import React, { useEffect } from "react";

import { Paper, Grid, Typography, Toolbar, Box } from "@mui/material";
import { Button, ButtonGroup } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import DashboardService from "../../../../services/dashboard.service";
import { useAppState } from "../../../../context/StateContext";
import { Chart, ChartType } from "../../../Generic/Chart";
import { textInfoB, boxBGColorA, white } from "../../DashStyleGuides";
import NoDataLabel from "../../NoDataLabel/index";
import buttonStyles from "../button.styles";
import { useStyles } from "./styles";
import { DateFilterTypes } from "../../../../util/enums";
import { translateMonthLabel } from "../../../../util/translateMonth";

/** Default Functional Component */
export default function QuotesPerformed({ isTodaySelected }: { isTodaySelected: boolean }) {
  const classes = useStyles();
  const buttonClass = buttonStyles();
  const { t } = useTranslation();
  const { currentUser } = useAppState();

  const [selIndex, setSelectedIndex] = React.useState<number>(1);
  const [dataOnX, setDataOnX] = React.useState<string[]>([]);
  const [dataOnY, setDataOnY] = React.useState<number[]>([]);

  useEffect(() => {
    if (isTodaySelected) {
      setSelectedIndex(isTodaySelected ? 0 : 1);
    }
  }, [isTodaySelected]);

  useQuery(
    ["dashboard.quote.performed", selIndex],
    () =>
      DashboardService.getQuotePerformed({
        dateFilterType: Object.values(DateFilterTypes)[selIndex],
        companyId: currentUser?.companyId,
      }),
    {
      onSuccess: (response) => {
        if (response && response?.data) {
          const xArrLabels: string[] = [];
          const yArrLabels: number[] = [];

          if (response.data.length === 0) {
            setDataOnX([]);
            setDataOnY([]);

            return; // means no data found
          }
          response.data.forEach((info) => {
            switch (selIndex) {
              case 0:
                if (info?.hour !== undefined) xArrLabels.push(funcHourLabelLoader(info?.hour));
                if (info?.count !== undefined) yArrLabels.push(info?.count);
                break;
              case 2:
                if (info?.year !== undefined) xArrLabels.push(info?.year?.toString());
                if (info?.count !== undefined) yArrLabels.push(info.count);
                break;
              default:
                if (info?.month) xArrLabels.push(translateMonthLabel(`${info?.month} ${info.year}`, t));
                if (info?.count !== undefined) yArrLabels.push(info.count);
                break;
            }
          });
          setDataOnX(xArrLabels);
          setDataOnY(yArrLabels);
        }
      },
      enabled: !!currentUser?.companyId,
    }
  );

  const funcHourLabelLoader = (hour: number) => {
    const convertedNum = hour.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    // eslint-disable-next-line no-nested-ternary
    return Number(convertedNum) < 9
      ? Number(convertedNum) === 0
        ? "12 AM"
        : `${convertedNum} AM`
      : `${convertedNum} PM`;
  };

  return (
    <>
      <Grid item xs={12}>
        <Paper className={classes.root} elevation={0}>
          <Toolbar className={classes.tblRoot} variant="dense">
            <Typography className={classes.title} color="textSecondary" data-cy="quotesPerformed">
              {t("quotesPerformed")}
            </Typography>
            <Box className={classes.bxFlexGrow} />
            <ButtonGroup color="info">
              {Object.keys(DateFilterTypes).map((filter, index) => (
                <Button
                  className={classnames({
                    [buttonClass.buttonContained]: selIndex === index,
                    [buttonClass.buttonOutlined]: selIndex !== index,
                  })}
                  key={index}
                  variant={selIndex === index ? "contained" : "outlined"}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedIndex(index);
                  }}
                >
                  {t(filter.toLowerCase())}
                </Button>
              ))}
            </ButtonGroup>
          </Toolbar>
          {dataOnX.length === 0 && dataOnY.length === 0 ? (
            <NoDataLabel />
          ) : (
            <div className={classnames("chart-container", classes.quotesPerformedWrapStyles)}>
              {dataOnX.length > 0 && dataOnY.length > 0 && (
                <Chart
                  id="lineGraphId"
                  chartType={ChartType.LINE}
                  graphOptions={{
                    responsive: true,
                    maintainAspectRatio: false,
                    fill: true,
                    pointStyle: "circle",
                    pointRadius: 6,
                    pointBorderWidth: 6,
                    pointBackgroundColor: white,
                    pointBorderColor: textInfoB,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        usePointStyle: true,
                        boxPadding: 3,
                      },
                    },
                  }}
                  data={{
                    labels: dataOnX,
                    datasets: [
                      {
                        data: dataOnY,
                        borderColor: textInfoB,
                        backgroundColor: boxBGColorA,
                        fill: true,
                        pointStyle: "circle",
                      },
                    ],
                  }}
                />
              )}
            </div>
          )}
        </Paper>
      </Grid>
    </>
  );
}
